import { AxiosResponse } from "axios";
import { Dispatch } from "@reduxjs/toolkit";

import {
  Module,
  EndPoint,
  putApiCall,
  getApiCall,
  postApiCall,
  deleteApiCall,
} from "../../../common/constants";
import {
  setLicenseNumberData,
  setCompanyProfileData,
  setDeletionRequestData,
  setFacilityLicenseListAPI,
  setCompanyProfileFacilityList,
} from "./slices";
import { RootState } from "../../../redux/store";
import { setLoading } from "../../../redux/global.slice";
import { SetSubmittingType } from "../../../redux/global.types";
import { apiSuccessCode } from "../../../common/constants/axios.instance";
import { checkInternetAndShowAlert, showAlert } from "../../../common/utils";
import {
  AnalyticsEvents,
  capturePosthogEvent,
} from "../../../common/utils/analytics";

export const getCompanyProfileData =
  (userId: any) => (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }
    dispatch(setLoading(true));
    getApiCall(
      Module.manufacturer,
      `${EndPoint.companyprofile}?userId=${userId}`,
      (response: AxiosResponse<any>) => {
        const { statusCode, data } = response.data;

        if (statusCode === apiSuccessCode.success) {
          dispatch(setCompanyProfileData(data));
        }
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const updateCompanyProfileData =
  (values: any, callback?: () => void) =>
  (dispatch: Dispatch<any>, getState: () => RootState) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    dispatch(setLoading(true));

    putApiCall(
      Module.manufacturer + EndPoint.companyprofile,
      values,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data?.statusCode === apiSuccessCode.ok) {
          const uid = getState().verifyEmail.userId;
          const fieldsChanged = Object.keys(values).filter(
            (k) => k !== "userId" && values[k] !== undefined,
          );
          capturePosthogEvent(AnalyticsEvents.ACCOUNT_SETTINGS_UPDATED, {
            user_id: uid,
            scope: "company_profile",
            fields_changed: fieldsChanged,
          });
          dispatch(setCompanyProfileData(values.userId));
          showAlert(1, data.message);
          if (callback) callback();
        }

        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const checkUniqueComapanyName =
  (values: any, setUniqueError: any, callback?: () => void) =>
  (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    dispatch(setLoading(true));

    postApiCall(
      Module.manufacturer + EndPoint.checkUniqueName,
      values,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data?.statusCode === apiSuccessCode.success) {
          setUniqueError(false);
          // showAlert(1, data.message);
          if (callback) callback();
        }

        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        setUniqueError(true);
        showAlert(2, error.message);
      },
    );
  };

export const checkUniqueLicenseNumber =
  (values: any, setUniqueLicenseError: any, callback?: () => void) =>
  (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    dispatch(setLoading(true));

    postApiCall(
      Module.manufacturer + EndPoint.checkUniqueLicenseNumber,
      values,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data?.statusCode === apiSuccessCode.success) {
          setUniqueLicenseError(false);
          if (callback) callback();
        }

        dispatch(setLoading(false));
      },
      (error: any) => {
        setUniqueLicenseError(true);
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const licenseApprovalRequest =
  (values: any, callback?: () => void) => (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    dispatch(setLoading(true));

    postApiCall(
      Module.manufacturer + EndPoint.licenseApproval,
      values,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data?.statusCode === apiSuccessCode.success) {
          if (callback) callback();
        }

        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const addLicense =
  (values: any, callback?: any) => (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }
    dispatch(setLoading(true));
    postApiCall(
      Module.manufacturer + EndPoint.license,
      values,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data?.statusCode === apiSuccessCode.success) {
          showAlert(1, data.message);
          if (callback) callback();
        }
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const deleteLicense = (
  licenseNumber: string,
  callback?: any,
  id?: string,
) => {
  return (dispatch: any, getState: any) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }
    dispatch(setLoading(true));
    deleteApiCall(
      `${Module.manufacturer}${EndPoint.license}?licenseNumber=${licenseNumber}`,
      "",

      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data?.statusCode === apiSuccessCode.success) {
          showAlert(1, data.message);
          dispatch(getCompanyProfileData(id));
          if (callback) callback();
        }
        dispatch(setLoading(false));
      },
      (error: any) => {
        showAlert(2, error.message);
        dispatch(setLoading(false));
      },
    );
  };
};

export const getLicenseNumberManually =
  (userId: any) => (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }
    dispatch(setLoading(true));
    getApiCall(
      Module.manufacturer,
      `${EndPoint.licenseAddedManually}?userId=${userId}`,
      (response: AxiosResponse<any>) => {
        const { statusCode, data } = response.data;

        if (statusCode === apiSuccessCode.success) {
          dispatch(setLicenseNumberData(data));
        }
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const updateLicenseNumberManually =
  (values: any, setSubmitting?: SetSubmittingType, callback?: () => void) =>
  (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }
    if (setSubmitting) setSubmitting(true);

    dispatch(setLoading(true));

    postApiCall(
      Module.manufacturer + `${EndPoint.manuallyUpdateLicense}`,
      values,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;

        if (data?.statusCode === apiSuccessCode.success) {
          dispatch(setLicenseNumberData(values.userId));
          showAlert(1, data.message);
          if (callback) callback();
        }

        dispatch(setLoading(false));
        if (setSubmitting) setSubmitting(false);
      },
      (error: any) => {
        dispatch(setLoading(false));
        if (setSubmitting) setSubmitting(false);
        showAlert(2, error.message);
      },
    );
  };

export const getDeletionRequests = () => (dispatch: Dispatch<any>) => {
  // Check for network connectivity
  if (!checkInternetAndShowAlert(showAlert)) {
    return;
  }
  dispatch(setLoading(true));
  getApiCall(
    Module.manufacturer,
    `${EndPoint.deletionRequestLicense}`,
    (response: AxiosResponse<any>) => {
      const { statusCode, data } = response.data;

      if (statusCode === apiSuccessCode.success) {
        dispatch(setDeletionRequestData(data));
      }
      dispatch(setLoading(false));
    },
    (error: any) => {
      dispatch(setLoading(false));
      showAlert(2, error.message);
    },
  );
};

export const getFacilityLicenseNumberThroughAPI =
  (callback?: () => void, type?: any) => (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }
    dispatch(setLoading(true));

    const url = `${EndPoint.importFacilityLicense}?type=${type}`;

    getApiCall(
      Module.manufacturer,
      url,
      (response: AxiosResponse<any>) => {
        const { statusCode, data } = response.data;
        if (statusCode === apiSuccessCode.success) {
          dispatch(setFacilityLicenseListAPI(data.facilityLicenseNumbers));
          if (callback) callback();
        }
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const getFacilitiesListing = () => (dispatch: Dispatch<any>) => {
  // Check for network connectivity
  if (!checkInternetAndShowAlert(showAlert)) {
    return;
  }
  dispatch(setLoading(true));
  getApiCall(
    Module.manufacturer,
    `${EndPoint.importedFacilityListing}`,
    (response: AxiosResponse<any>) => {
      const { statusCode, data } = response.data;

      if (statusCode === apiSuccessCode.success) {
        dispatch(setCompanyProfileFacilityList(data));
      }
      dispatch(setLoading(false));
    },
    (error: any) => {
      dispatch(setLoading(false));
      showAlert(2, error.message);
    },
  );
};
